<script setup>
import { computed, ref } from 'vue';
import { formatBytes } from 'shared/helpers/FileHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['removeAttachment']);

const nonRecordedAudioAttachments = computed(() => {
  return props.attachments.filter(attachment => !attachment?.isRecordedAudio);
});

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isRecordedAudio)
);

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

const formatFileSize = file => {
  const size = file.byte_size || file.size;
  return formatBytes(size, 0);
};

const isTypeImage = file => {
  const type = file.content_type || file.type;
  return type?.includes('image') ?? false;
};

const isTypeVideo = file => {
  const type = file.content_type || file.type;
  return type?.includes('video') ?? false;
};

const isPreviewable = file => isTypeImage(file) || isTypeVideo(file);

const fileName = file => {
  return file.filename || file.name;
};

// Preview modal
const showPreview = ref(false);
const previewAttachment = ref(null);

function openPreview(attachment) {
  const resource = attachment.resource;
  if (!isPreviewable(resource)) return;

  previewAttachment.value = {
    file_type: isTypeImage(resource) ? 'image' : 'video',
    data_url: attachment.thumb,
    extension: (resource.name ?? '').split('.').pop(),
    message_id: -1,
  };
  showPreview.value = true;
}

function closePreview() {
  showPreview.value = false;
  previewAttachment.value = null;
}
</script>

<template>
  <div class="flex flex-wrap gap-y-1 gap-x-2 overflow-auto max-h-[12.5rem]">
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id"
      class="flex items-center p-1 bg-n-slate-3 gap-1 rounded-md w-[15rem]"
    >
      <div
        class="max-w-[4rem] flex-shrink-0 w-6 flex items-center"
        :class="isPreviewable(attachment.resource) ? 'cursor-pointer' : ''"
        @click="openPreview(attachment)"
      >
        <img
          v-if="isTypeImage(attachment.resource)"
          class="object-cover w-6 h-6 rounded-sm"
          :src="attachment.thumb"
        />
        <span
          v-else-if="isTypeVideo(attachment.resource)"
          class="relative w-6 h-6 text-lg text-left -top-px"
        >
          🎬
        </span>
        <span v-else class="relative w-6 h-6 text-lg text-left -top-px">
          📄
        </span>
      </div>
      <div
        class="max-w-3/5 min-w-[50%] overflow-hidden text-ellipsis"
        :class="isPreviewable(attachment.resource) ? 'cursor-pointer' : ''"
        @click="openPreview(attachment)"
      >
        <span
          class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap"
        >
          {{ fileName(attachment.resource) }}
        </span>
      </div>
      <div class="w-[30%] justify-center">
        <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center">
        <Button
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>
  </div>

  <GalleryView
    v-if="showPreview && previewAttachment"
    v-model:show="showPreview"
    :attachment="previewAttachment"
    :all-attachments="[previewAttachment]"
    @close="closePreview"
  />
</template>
